import React from 'react';
import {
  Caption,
  Chip,
  ChipProps,
  GenericComponent,
  GenericComponentType,
  Headline,
  Icon,
  IconType,
  PropsWithGenericComponent,
  timeUtils,
} from '@sato/components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';

import { localizationStore } from '@stores/localization';

export type MessagePreviewProps<T extends GenericComponentType> =
  PropsWithGenericComponent<
    {
      title: string;
      timestamp: string | Date;
      statusChip?: ChipProps;
      /** @default 'left' */
      iconPosition?: 'top' | 'left';
      className?: string;
      /** Whether should show a chevron and have hover styles */
      isLink?: boolean;
      isLarge?: boolean;
      icon?: IconType;
    },
    T
  >;

/** Component that displays basic information about a message / incident. */
export const MessagePreview = observer(
  <T extends GenericComponentType = 'div'>({
    iconPosition = 'left',
    wrapperComponent,
    isLink,
    isLarge,
    statusChip,
    timestamp,
    title,
    icon,
    ...rest
  }: MessagePreviewProps<T>) => (
    <GenericComponent
      {...rest}
      wrapperComponent={wrapperComponent ?? 'div'}
      className={classNames('group', rest.className)}
    >
      <div className="flex w-full flex-wrap items-center gap-3">
        {icon && (
          <div className={classNames(iconPosition === 'top' && 'basis-full')}>
            <Icon
              className={classNames(
                isLarge ? 'text-[3.5rem] md:text-[5rem]' : 'text-[2.75rem]'
              )}
              icon={icon}
            />
          </div>
        )}

        <div className="flex-1">
          {timestamp && (
            <Caption className="text-black-medium-text">
              {timeUtils.formatDate({
                date: timestamp,
                dateFormat: timeUtils.DATE_FORMATS.DATE,
                locale: localizationStore.language,
              })}
            </Caption>
          )}

          <Headline
            className={classNames(
              'my-1 break-words',
              isLink && 'group-hover:!text-brand-interactive-ultradark'
            )}
            level={isLarge ? 2 : 3}
          >
            {title}
          </Headline>
        </div>

        {isLink && (
          <Icon icon="chevronRight" size="sm" className="self-center" />
        )}
      </div>

      {statusChip && (
        <Chip
          {...statusChip}
          className={classNames(
            statusChip.className,
            'pointer-events-none mt-3',
            iconPosition === 'left' && 'ml-8'
          )}
        />
      )}
    </GenericComponent>
  )
);
